import { listCourses } from "./courseHelpers"
import { listOrgEnrollments } from "./enrollmentHelpers"
import { listOrgOrders } from "./orderHelpers"
import { listPrograms } from "./programHelpers"

const formatCompletion = (enrollments) => {
  if (!enrollments?.length) return 0
  const completed = enrollments?.filter((e) => (e?.progressPercent || 0) >= 100)?.length
  return Math.round((completed / enrollments.length) * 100)
}

const sumRevenue = (orders, productIds) => {
  return orders
    ?.filter((o) => o?.status === "paid" && productIds?.includes(o?.productId))
    ?.reduce((sum, o) => sum + (o?.amount || 0), 0) || 0
}

// Builds one row per Course and one per Program, each with real Learners,
// Completion %, and Revenue. Programs aggregate across their bundled
// courseIds — a program with no courses assigned yet legitimately shows
// zeros rather than fabricated numbers.
export const computeProductPerformance = async (orgId) => {
  const [{ courses }, { enrollments }, { orders }, { programs }] = await Promise.all([
    listCourses(orgId),
    listOrgEnrollments(orgId),
    listOrgOrders(orgId),
    listPrograms(orgId),
  ])

  const publishedCourses = courses?.filter((c) => c?.status === "published") || []

  const courseRows = publishedCourses?.map((course) => {
    const courseEnrollments = enrollments?.filter((e) => e?.courseId === course?.courseId) || []
    const learnerIds = new Set(courseEnrollments?.map((e) => e?.userId))
    return {
      id: course?.courseId,
      name: course?.title || "Untitled Course",
      type: "Course",
      learners: learnerIds.size,
      completion: formatCompletion(courseEnrollments),
      revenue: sumRevenue(orders, [course?.courseId]),
      viewProductPath: `/course-preview?orgId=${orgId}&courseId=${course?.courseId}`,
      analyticsTab: "intelligence-courses",
    }
  })

  const activePrograms = programs?.filter((p) => p?.status !== "archived") || []

  const programRows = activePrograms?.map((program) => {
    const bundledIds = program?.courseIds || []
    const programEnrollments = enrollments?.filter((e) => bundledIds?.includes(e?.courseId)) || []
    const learnerIds = new Set(programEnrollments?.map((e) => e?.userId))
    const revenue = sumRevenue(orders, [...bundledIds, program?.programId])
    return {
      id: program?.programId,
      name: program?.title || "Untitled Program",
      type: program?.programType || "Program",
      learners: learnerIds.size,
      completion: formatCompletion(programEnrollments),
      revenue,
      viewProductPath: null, // no public program page yet
      analyticsTab: "intelligence-programs",
    }
  })

  return [...courseRows, ...programRows]?.sort((a, b) => b?.learners - a?.learners)
}